import type { InvoiceDetailPeekContext } from "@/components/commissions/InvoiceDetailHoverHint";
import type {
  BonusInvoiceStatus,
  CommissionInvoiceStatus,
  CommissionRunLineRow,
} from "./commissionsApi";

/** Fields sufficient to build an invoice hover peek — shared by invoice status and run detail rows. */
export type InvoiceDetailPeekSource = {
  invoice_mv_id: number;
  invoice_number: string | null;
  invoice_qb_doc_number?: string | null;
  invoice_date?: string | null;
  sales_order_number?: string | null;
  sales_order_date?: string | null;
  sales_order_payment_terms_label?: string | null;
  due_date?: string | null;
  total_amount?: number | null;
  qb_line_total_mismatch?: boolean;
  qb_compared_total?: number | null;
  qb_line_total_mismatch_reason?: string | null;
  end_user_name?: string | null;
  /** GOSI invoice end user from Megaventory — used when POS resale overlays a different end user. */
  invoice_end_user_name?: string | null;
  rep_agency_name?: string | null;
  fully_paid_date?: string | null;
  commission_status?: CommissionInvoiceStatus | null;
  bonus_status?: BonusInvoiceStatus | null;
  source_kind?: string | null;
  commission_amount?: string | number | null;
  basis_amount?: string | number | null;
  detail?: Record<string, unknown> | null;
};

const BONUS_RUN_LINE_SOURCE_KINDS = new Set(["tier_bonus", "new_end_user_bonus"]);

function parsePeekNumber(value: string | number | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePeekName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : null;
}

function namesDiffer(left: string | null | undefined, right: string | null | undefined): boolean {
  const normalizedLeft = normalizePeekName(left);
  const normalizedRight = normalizePeekName(right);
  if (normalizedLeft == null || normalizedRight == null) {
    return false;
  }
  return normalizedLeft.localeCompare(normalizedRight, undefined, { sensitivity: "accent" }) !== 0;
}

function detailDateString(detail: Record<string, unknown> | null | undefined, key: string): string | null {
  const raw = detail?.[key];
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  return trimmed === "" ? null : trimmed.slice(0, 10);
}

export function buildInvoiceDetailPeekContext(
  source: InvoiceDetailPeekSource,
): InvoiceDetailPeekContext {
  const runLineAmount = parsePeekNumber(source.commission_amount);
  const isBonusLine =
    source.source_kind != null && BONUS_RUN_LINE_SOURCE_KINDS.has(source.source_kind);
  const posResaleEndUser =
    source.source_kind === "stocking_resale" ? normalizePeekName(source.end_user_name) : null;
  const gosiEndUser =
    source.source_kind === "stocking_resale" && namesDiffer(source.end_user_name, source.invoice_end_user_name)
      ? normalizePeekName(source.invoice_end_user_name)
      : null;
  const stockPaidOn =
    source.source_kind === "stocking_resale"
      ? detailDateString(source.detail, "stockPaidOn")
      : null;
  const posLineTotal =
    source.source_kind === "stocking_resale" ? parsePeekNumber(source.basis_amount) : null;

  return {
    invoice_mv_id: source.invoice_mv_id,
    invoice_number: source.invoice_number ?? "",
    invoice_qb_doc_number: source.invoice_qb_doc_number,
    invoice_date: source.invoice_date ?? null,
    sales_order_number: source.sales_order_number,
    sales_order_date: source.sales_order_date,
    sales_order_payment_terms_label: source.sales_order_payment_terms_label,
    due_date: source.due_date,
    total_amount: posLineTotal ?? source.total_amount,
    total_label: posLineTotal != null ? "POS total" : undefined,
    qb_line_total_mismatch: source.qb_line_total_mismatch,
    qb_compared_total: source.qb_compared_total,
    qb_line_total_mismatch_reason: source.qb_line_total_mismatch_reason,
    end_user_name: posResaleEndUser ?? source.end_user_name ?? null,
    pos_resale_end_user_name: posResaleEndUser,
    gosi_end_user_name: gosiEndUser,
    rep_agency_name: source.rep_agency_name ?? null,
    fully_paid_date: stockPaidOn ?? source.fully_paid_date ?? null,
    fully_paid_label: stockPaidOn != null ? "Stock paid" : undefined,
    commission_status: source.commission_status,
    bonus_status: source.bonus_status,
    run_commission_amount: isBonusLine ? null : runLineAmount,
    run_bonus_amount: isBonusLine ? runLineAmount : null,
  };
}

export function runLineInvoicePeek(
  line: Omit<InvoiceDetailPeekSource, "invoice_mv_id"> & {
    invoice_mv_id: number | null | undefined;
  },
): InvoiceDetailPeekContext | undefined {
  if (line.invoice_mv_id == null || !Number.isFinite(line.invoice_mv_id) || line.invoice_mv_id <= 0) {
    return undefined;
  }

  return buildInvoiceDetailPeekContext({
    ...line,
    invoice_mv_id: line.invoice_mv_id,
  });
}

export function runLineInvoicePeekFromRow(line: CommissionRunLineRow): InvoiceDetailPeekContext | undefined {
  return runLineInvoicePeek({
    invoice_mv_id: line.invoice_mv_id,
    invoice_number: line.invoice_number,
    invoice_qb_doc_number: line.invoice_qb_doc_number,
    invoice_date: line.invoice_date,
    sales_order_number: line.sales_order_number,
    sales_order_date: line.sales_order_date,
    sales_order_payment_terms_label: line.sales_order_payment_terms_label,
    due_date: line.due_date,
    total_amount: line.total_amount,
    qb_line_total_mismatch: line.qb_line_total_mismatch,
    qb_compared_total: line.qb_compared_total,
    qb_line_total_mismatch_reason: line.qb_line_total_mismatch_reason,
    end_user_name: line.end_user_name,
    invoice_end_user_name: line.invoice_end_user_name,
    rep_agency_name: line.rep_agency_name,
    fully_paid_date: line.fully_paid_date,
    source_kind: line.source_kind,
    commission_amount: line.commission_amount,
    basis_amount: line.basis_amount,
    detail: line.detail,
  });
}
